/**
 * Seitenbudget im echten PDF messen.
 *
 * Das Factsheet hat vier logische Seiten (je ein react-pdf `<Page>`). Läuft eine über,
 * hängt react-pdf eine physische Seite an: beim zu langen Monatskommentar rutscht der
 * umbruchgeschützte Fußblock von Seite 1 allein auf eine neue Seite, das PDF hat 5 statt
 * 4 Seiten und Seite 2 wirkt leer (Basistest SQUAD 02.10.2026). Die HTML-Vorschau taugt
 * dafür nicht — sie ist schon im Normalfall höher als A4.
 *
 * Eine fixierte Sonde je logischer Seite (`PageLayoutProbe`) meldet, auf welchen
 * physischen Seiten sie landet; hier wird daraus die Warnung für den Vorschaudialog.
 */

export const SQUAD_LOGICAL_PAGES = 4;

export type SquadPageLayout = {
  totalPages: number;
  /** Logische Seiten (1–4), die mehr als eine physische Seite belegen. */
  overflowing: number[];
};

export function summarizeSquadPageLayout(spans: Map<number, Set<number>>, totalPages: number): SquadPageLayout {
  const overflowing = Array.from(spans.entries())
    .filter(([, physical]) => physical.size > 1)
    .map(([logical]) => logical)
    .sort((a, b) => a - b);
  return { totalPages, overflowing };
}

const PAGE_HINTS: Record<number, string> = {
  1: 'Seite 1 läuft über – der Fußblock rutscht allein auf eine neue Seite, Seite 2 wirkt dann leer. Meist ist der Monatskommentar zu lang – bitte im Excel kürzen.',
  2: 'Seite 2 (Portfolio) läuft über – im Excel weniger Zeilen oder Bereiche befüllen.',
  3: 'Seite 3 läuft über.',
  4: 'Seite 4 (Hinweise) läuft über.',
};

/** Text für den Vorschaudialog, `null` wenn das PDF ins Budget passt. */
export function squadPageLayoutWarning(layout: SquadPageLayout | null | undefined): string | null {
  if (!layout || layout.totalPages <= SQUAD_LOGICAL_PAGES) return null;
  const hints = layout.overflowing.map((p) => PAGE_HINTS[p]).filter(Boolean);
  return [`Das PDF hat ${layout.totalPages} statt ${SQUAD_LOGICAL_PAGES} Seiten.`, ...hints].join(' ');
}
